import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Resultados from "@/pages/Resultados";
import Agents from "@/pages/Agents";
import AgentConsole from "@/pages/AgentConsole";
import Departments from "@/pages/Departments";
import SpecialistConsole from "@/pages/SpecialistConsole";
import Council from "@/pages/Council";
import SystemMode from "@/pages/SystemMode";
import Templates from "@/pages/Templates";
import Members from "@/pages/Members";
import Budgets from "@/pages/Budgets";
import Workflows from "@/pages/Workflows";
import Evaluations from "@/pages/Evaluations";
import ApiKeys from "@/pages/ApiKeys";
import LlmSettings from "@/pages/LlmSettings";
import Onboarding from "@/pages/Onboarding";
import Pricing from "@/pages/Pricing";
import Landing from "@/pages/Landing";
import SectorLanding from "@/pages/SectorLanding";
import LeadService from "@/pages/LeadService";
import Legal from "@/pages/Legal";
import CookieConsent from "@/components/CookieConsent";
import Asistente from "@/pages/Asistente";
import PublicAssistant from "@/pages/PublicAssistant";
import Acciones from "@/pages/Acciones";
import Captacion from "@/pages/Captacion";
import Integrations from "@/pages/Integrations";
import Automations from "@/pages/Automations";
import Reports from "@/pages/Reports";
import Approvals from "@/pages/Approvals";
import Audit from "@/pages/Audit";
import Knowledge from "@/pages/Knowledge";
import Security from "@/pages/Security";
import CRM from "@/pages/CRM";
import Tasks from "@/pages/Tasks";
import Tools from "@/pages/Tools";
import Policies from "@/pages/Policies";
import Playbooks from "@/pages/Playbooks";
import Incidents from "@/pages/Incidents";
import Events from "@/pages/Events";
import Decisions from "@/pages/Decisions";
import Cost from "@/pages/Cost";
import Frameworks from "@/pages/Frameworks";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--text-dim)] font-mono text-sm">
        Autenticando…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Home() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Precios: pública, cualquiera puede verla sin registrarse */}
            <Route path="/precios" element={<Pricing />} />
            <Route path="/a/:slug" element={<PublicAssistant />} />
            <Route path="/welcome" element={<Protected><Onboarding /></Protected>} />
            <Route path="/" element={<Home />} />
            <Route path="/para/:slug" element={<SectorLanding />} />
            {/* Servicio gestionado — landing de captación pública */}
            <Route path="/servicio" element={<LeadService />} />
            {/* Legales — públicas */}
            <Route path="/privacidad" element={<Legal doc="privacidad" />} />
            <Route path="/cookies" element={<Legal doc="cookies" />} />
            <Route path="/terminos" element={<Legal doc="terminos" />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="resultados" element={<Resultados />} />
              <Route path="agents" element={<Agents />} />
              <Route path="agents/:agentId" element={<AgentConsole />} />
              <Route path="departments" element={<Departments />} />
              <Route path="specialists/:specId" element={<SpecialistConsole />} />
              <Route path="council" element={<Council />} />
              <Route path="system" element={<SystemMode />} />
              <Route path="templates" element={<Templates />} />
              <Route path="members" element={<Members />} />
              <Route path="budgets" element={<Budgets />} />
              <Route path="workflows" element={<Workflows />} />
              <Route path="evaluations" element={<Evaluations />} />
              <Route path="api-keys" element={<ApiKeys />} />
              <Route path="settings/llm" element={<LlmSettings />} />
              <Route path="asistente" element={<Asistente />} />
              <Route path="acciones" element={<Acciones />} />
              <Route path="captacion" element={<Captacion />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="automations" element={<Automations />} />
              <Route path="reports" element={<Reports />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="audit" element={<Audit />} />
              <Route path="knowledge" element={<Knowledge />} />
              <Route path="security" element={<Security />} />
              <Route path="crm" element={<CRM />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="tools" element={<Tools />} />
              <Route path="policies" element={<Policies />} />
              <Route path="playbooks" element={<Playbooks />} />
              <Route path="incidents" element={<Incidents />} />
              <Route path="events" element={<Events />} />
              <Route path="decisions" element={<Decisions />} />
              <Route path="cost" element={<Cost />} />
              <Route path="frameworks" element={<Frameworks />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieConsent />
          <Toaster
            position="top-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#131722",
                border: "1px solid #272D3D",
                color: "#F8FAFC",
                borderRadius: 0,
                fontFamily: "IBM Plex Sans, sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
